import { useState } from 'react';
import {
  Download,
  FileSpreadsheet,
  FileText,
  FileCode,
  Archive,
  CheckCircle2,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { useStore } from '../../stores/useStore';
import {
  exportToCSV,
  exportToExcel,
  exportToHTML,
  exportAllAsZip,
} from '../../lib/export';
import clsx from 'clsx';

const CATEGORIES = [
  { id: 'inclusion', label: 'Inclusion Only', color: 'accent' },
  { id: 'exclusion', label: 'Exclusion Only', color: 'danger' },
  { id: 'both', label: 'Both', color: 'warning' },
  { id: 'none', label: 'None', color: 'neutral' },
] as const;

export function ExportView() {
  const { results, inclusionKeywords, exclusionKeywords, setActiveTab } = useStore();

  const [selectedCategories, setSelectedCategories] = useState<string[]>([
    'inclusion',
    'exclusion',
    'both',
    'none',
  ]);
  const [includeHighlighting, setIncludeHighlighting] = useState(true);
  const [includeStatistics, setIncludeStatistics] = useState(true);
  const [exporting, setExporting] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);

  if (!results) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-center">
        <AlertCircle className="w-16 h-16 text-slate-300 dark:text-slate-600 mb-4" />
        <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
          No Results to Export
        </h3>
        <p className="text-slate-500 dark:text-slate-400 mb-4">
          Run an analysis first to export results
        </p>
        <button onClick={() => setActiveTab('keywords')} className="btn-primary">
          Go to Keywords
        </button>
      </div>
    );
  }

  const toggleCategory = (id: string) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const getPapersForExport = () => {
    const papers = [];
    if (selectedCategories.includes('inclusion')) {
      papers.push(...results.inclusionOnly);
    }
    if (selectedCategories.includes('exclusion')) {
      papers.push(...results.exclusionOnly);
    }
    if (selectedCategories.includes('both')) {
      papers.push(...results.both);
    }
    if (selectedCategories.includes('none')) {
      papers.push(...results.none);
    }
    return papers;
  };

  const handleExport = async (format: 'csv' | 'excel' | 'html' | 'zip') => {
    setExporting(format);
    setExportSuccess(null);

    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `papers_refiner_${timestamp}`;

    try {
      const papers = getPapersForExport();

      switch (format) {
        case 'csv':
          exportToCSV(papers, filename, true);
          break;

        case 'excel':
          await exportToExcel(results, filename, {
            includeStatistics,
            categories: selectedCategories as any[],
          });
          break;

        case 'html':
          const categoryLabel = selectedCategories.length === 4
            ? 'All Categories'
            : selectedCategories.map((c) => c.charAt(0).toUpperCase() + c.slice(1)).join(', ');

          exportToHTML(papers, filename, `${categoryLabel} - Analysis Results`, {
            includeHighlighting,
            inclusionKeywords: inclusionKeywords.map((k) => k.text),
            exclusionKeywords: exclusionKeywords.map((k) => k.text),
          });
          break;

        case 'zip':
          await exportAllAsZip(results, filename, {
            includeHighlighting,
            inclusionKeywords: inclusionKeywords.map((k) => k.text),
            exclusionKeywords: exclusionKeywords.map((k) => k.text),
          });
          break;
      }

      setExportSuccess(format);
      setTimeout(() => setExportSuccess(null), 3000);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExporting(null);
    }
  };

  const exportOptions = [
    {
      id: 'csv',
      icon: FileText,
      title: 'CSV',
      description: 'Comma-separated values, compatible with all spreadsheet apps',
      color: 'bg-blue-500',
    },
    {
      id: 'excel',
      icon: FileSpreadsheet,
      title: 'Excel',
      description: 'XLSX format with multiple sheets and statistics',
      color: 'bg-green-600',
    },
    {
      id: 'html',
      icon: FileCode,
      title: 'HTML',
      description: 'Web page with keyword highlighting, viewable in any browser',
      color: 'bg-orange-500',
    },
    {
      id: 'zip',
      icon: Archive,
      title: 'Complete ZIP',
      description: 'All formats bundled together with analysis log',
      color: 'bg-purple-600',
    },
  ] as const;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
          Export Results
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Download your filtered papers in various formats
        </p>
      </div>

      {/* Category selection */}
      <div className="card p-6">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">
          Select Categories to Export
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {CATEGORIES.map((cat) => {
            const count =
              cat.id === 'inclusion'
                ? results.inclusionOnly.length
                : cat.id === 'exclusion'
                ? results.exclusionOnly.length
                : cat.id === 'both'
                ? results.both.length
                : results.none.length;

            const isSelected = selectedCategories.includes(cat.id);

            return (
              <button
                key={cat.id}
                onClick={() => toggleCategory(cat.id)}
                className={clsx(
                  'p-4 rounded-xl border-2 transition-all duration-200 text-left',
                  isSelected
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={clsx(
                      'text-sm font-medium',
                      isSelected
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-slate-700 dark:text-slate-300'
                    )}
                  >
                    {cat.label}
                  </span>
                  <div
                    className={clsx(
                      'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors',
                      isSelected
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-slate-300 dark:border-slate-600'
                    )}
                  >
                    {isSelected && (
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    )}
                  </div>
                </div>
                <div className="text-2xl font-bold text-slate-900 dark:text-white">
                  {count.toLocaleString()}
                </div>
              </button>
            );
          })}
        </div>

        {/* Select all/none buttons */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={() =>
              setSelectedCategories(['inclusion', 'exclusion', 'both', 'none'])
            }
            className="btn-ghost text-sm"
          >
            Select All
          </button>
          <button
            onClick={() => setSelectedCategories([])}
            className="btn-ghost text-sm"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Export options */}
      <div className="card p-6">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">
          Export Options
        </h3>

        <div className="space-y-3 mb-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={includeHighlighting}
              onChange={(e) => setIncludeHighlighting(e.target.checked)}
              className="w-5 h-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-slate-700 dark:text-slate-300">
              Include keyword highlighting (HTML export)
            </span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={includeStatistics}
              onChange={(e) => setIncludeStatistics(e.target.checked)}
              className="w-5 h-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-slate-700 dark:text-slate-300">
              Include statistics sheet (Excel export)
            </span>
          </label>
        </div>

        {/* Format buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {exportOptions.map((option) => {
            const isExporting = exporting === option.id;
            const isSuccess = exportSuccess === option.id;

            return (
              <button
                key={option.id}
                onClick={() => handleExport(option.id)}
                disabled={selectedCategories.length === 0 || exporting !== null}
                className={clsx(
                  'relative p-5 rounded-xl border-2 transition-all duration-200 text-left',
                  'hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed',
                  isSuccess
                    ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-primary-300 dark:hover:border-primary-700'
                )}
              >
                <div
                  className={clsx(
                    'w-12 h-12 rounded-xl flex items-center justify-center mb-3',
                    option.color
                  )}
                >
                  {isExporting ? (
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  ) : isSuccess ? (
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  ) : (
                    <option.icon className="w-6 h-6 text-white" />
                  )}
                </div>

                <h4 className="font-semibold text-slate-900 dark:text-white mb-1">
                  {option.title}
                </h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {option.description}
                </p>

                {isSuccess && (
                  <div className="absolute top-2 right-2">
                    <span className="badge-success">Downloaded!</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Selected papers count */}
        <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {selectedCategories.length === 0 ? (
              <span className="text-amber-600 dark:text-amber-400">
                Please select at least one category to export
              </span>
            ) : (
              <>
                <span className="font-medium text-slate-700 dark:text-slate-300">
                  {getPapersForExport().length.toLocaleString()}
                </span>{' '}
                papers selected for export
              </>
            )}
          </p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setActiveTab('results')}
          className="btn-ghost"
        >
          ← Back to Results
        </button>

        <button
          onClick={() => handleExport('zip')}
          disabled={selectedCategories.length === 0 || exporting !== null}
          className="btn-primary"
        >
          {exporting === 'zip' ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              Download All
            </>
          )}
        </button>
      </div>
    </div>
  );
}
